import { createTheme, ThemeOptions, alpha } from '@mui/material/styles';

const m3Light = {
  primary: '#6750A4', onPrimary: '#FFFFFF', primaryContainer: '#EADDFF', onPrimaryContainer: '#21005D',
  secondary: '#625B71', onSecondary: '#FFFFFF', secondaryContainer: '#E8DEF8', onSecondaryContainer: '#1D192B',
  error: '#B3261E', onError: '#FFFFFF', errorContainer: '#F9DEDC', onErrorContainer: '#410E0B',
  surface: '#FEF7FF', onSurface: '#1C1B1F', surfaceVariant: '#E7E0EC', onSurfaceVariant: '#49454F',
  surfaceContainerLow: '#F7F2FA', surfaceContainer: '#F3EDF7', surfaceContainerHigh: '#ECE6F0',
  outline: '#79747E', outlineVariant: '#CAC4D0',
  background: '#FEF7FF', onBackground: '#1C1B1F',
  inverseSurface: '#313033', inverseOnSurface: '#F4EFF4',
};

const m3Dark = {
  primary: '#D0BCFF', onPrimary: '#381E72', primaryContainer: '#4F378B', onPrimaryContainer: '#EADDFF',
  secondary: '#CCC2DC', onSecondary: '#332D41', secondaryContainer: '#4A4458', onSecondaryContainer: '#E8DEF8',
  error: '#F2B8B5', onError: '#601410', errorContainer: '#8C1D18', onErrorContainer: '#F9DEDC',
  surface: '#141218', onSurface: '#E6E0E9', surfaceVariant: '#49454F', onSurfaceVariant: '#CAC4D0',
  surfaceContainerLow: '#1D1B20', surfaceContainer: '#211F26', surfaceContainerHigh: '#2B2930',
  outline: '#938F99', outlineVariant: '#49454F',
  background: '#141218', onBackground: '#E6E0E9',
  inverseSurface: '#E6E0E9', inverseOnSurface: '#322F35',
};

const s1 = '0px 1px 2px rgba(0,0,0,.3), 0px 1px 3px 1px rgba(0,0,0,.15)';
const s2 = '0px 1px 2px rgba(0,0,0,.3), 0px 2px 6px 2px rgba(0,0,0,.15)';
const s3 = '0px 4px 8px 3px rgba(0,0,0,.15), 0px 1px 3px rgba(0,0,0,.3)';

const getThemeOptions = (mode: 'light' | 'dark'): ThemeOptions => {
  const c = mode === 'light' ? m3Light : m3Dark;
  return {
    palette: {
      mode,
      primary:    { main: c.primary,   contrastText: c.onPrimary,   light: c.primaryContainer,   dark: c.onPrimaryContainer   },
      secondary:  { main: c.secondary, contrastText: c.onSecondary, light: c.secondaryContainer, dark: c.onSecondaryContainer },
      error:      { main: c.error,     contrastText: c.onError,     light: c.errorContainer,     dark: c.onErrorContainer     },
      background: { default: c.background, paper: c.surfaceContainerLow },
      text:       { primary: c.onSurface, secondary: c.onSurfaceVariant, disabled: alpha(c.onSurface, 0.38) },
      divider:    c.outlineVariant,
    },
    typography: {
      fontFamily: '"Roboto", sans-serif',
      h1: { fontSize: '57px', lineHeight: '64px', fontWeight: 400, letterSpacing: '-0.25px' },
      h2: { fontSize: '45px', lineHeight: '52px', fontWeight: 400 },
      h3: { fontSize: '36px', lineHeight: '44px', fontWeight: 400 },
      h4: { fontSize: '32px', lineHeight: '40px', fontWeight: 400 },
      h5: { fontSize: '28px', lineHeight: '36px', fontWeight: 400 },
      h6: { fontSize: '24px', lineHeight: '32px', fontWeight: 400 },
      subtitle1: { fontSize: '22px', lineHeight: '28px', fontWeight: 400 },
      subtitle2: { fontSize: '16px', lineHeight: '24px', fontWeight: 500, letterSpacing: '0.15px' },
      body1:     { fontSize: '16px', lineHeight: '24px', fontWeight: 400, letterSpacing: '0.5px'  },
      body2:     { fontSize: '14px', lineHeight: '20px', fontWeight: 400, letterSpacing: '0.25px' },
      button:    { fontSize: '14px', lineHeight: '20px', fontWeight: 500, letterSpacing: '0.1px', textTransform: 'none' as const },
      caption:   { fontSize: '12px', lineHeight: '16px', fontWeight: 400, letterSpacing: '0.4px'  },
      overline:  { fontSize: '11px', lineHeight: '16px', fontWeight: 500, letterSpacing: '0.5px'  },
    },
    shape: { borderRadius: 16 },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 20, textTransform: 'none', fontWeight: 500, paddingInline: 24, minHeight: 40 },
          contained: { backgroundColor: c.primary, color: c.onPrimary, '&:hover': { backgroundColor: c.primary, boxShadow: 'inset 0 0 0 100vmax rgba(255,255,255,0.08)' } },
          outlined:  { borderColor: c.outline, color: c.primary, '&:hover': { backgroundColor: alpha(c.primary, 0.08) } },
          text:      { color: c.primary, '&:hover': { backgroundColor: alpha(c.primary, 0.08) } },
        },
      },
      MuiIconButton: {
        styleOverrides: { root: { borderRadius: 20, '&:hover': { backgroundColor: alpha(c.onSurface, 0.08) } } },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { borderRadius: 12, backgroundColor: c.surfaceContainerLow, border: '1px solid ' + c.outlineVariant } },
      },
      MuiCardContent: { styleOverrides: { root: { padding: 16 } } },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none', backgroundColor: c.surface },
          rounded: { borderRadius: 12 },
          elevation1: { boxShadow: s1 },
          elevation2: { boxShadow: s2 },
          elevation3: { boxShadow: s3 },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'default' },
        styleOverrides: { root: { backgroundColor: c.surfaceContainer, color: c.onSurface, borderBottom: '1px solid ' + c.outlineVariant } },
      },
      MuiToolbar: { styleOverrides: { root: { minHeight: 64 } } },
      MuiDrawer: {
        styleOverrides: { paper: { backgroundColor: c.surfaceContainerLow, borderRight: '1px solid ' + c.outlineVariant } },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 28, marginInline: 8, paddingInline: 16, minHeight: 56,
            '&:hover': { backgroundColor: alpha(c.onSurface, 0.08) },
            '&.Mui-selected': {
              backgroundColor: c.secondaryContainer, color: c.onSecondaryContainer,
              '&:hover': { backgroundColor: alpha(c.secondaryContainer, 0.92) },
              '& .MuiListItemIcon-root': { color: c.onSecondaryContainer },
            },
          },
        },
      },
      MuiListItemIcon: { styleOverrides: { root: { minWidth: 40, color: c.onSurfaceVariant } } },
      MuiTextField: { defaultProps: { variant: 'outlined' } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: c.outline },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: c.onSurface },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: c.primary, borderWidth: 2 },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8, height: 32 },
          filled: { backgroundColor: c.secondaryContainer, color: c.onSecondaryContainer },
          outlined: { borderColor: c.outline, color: c.onSurface },
        },
      },
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: 28, backgroundColor: c.surfaceContainerHigh, backgroundImage: 'none' } },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { fontSize: '24px', fontWeight: 400, lineHeight: '32px', paddingBottom: 16 } },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: 500, fontSize: '14px', letterSpacing: '0.1px', minHeight: 48 } },
      },
      MuiTabs: { styleOverrides: { indicator: { height: 3, borderRadius: '3px 3px 0 0' } } },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: c.inverseSurface, color: c.inverseOnSurface, borderRadius: 4, fontSize: '12px', padding: '4px 8px' },
          arrow: { color: c.inverseSurface },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: { padding: 8 },
          track: { borderRadius: 12, backgroundColor: c.surfaceVariant, opacity: 1 },
          thumb: { boxShadow: 'none' },
          switchBase: { '&.Mui-checked + .MuiSwitch-track': { backgroundColor: c.primary, opacity: 1 } },
        },
      },
      MuiAvatar: {
        styleOverrides: { root: { backgroundColor: c.primaryContainer, color: c.onPrimaryContainer, fontWeight: 500 } },
      },
      MuiDivider: { styleOverrides: { root: { borderColor: c.outlineVariant } } },
      MuiTableHead: {
        styleOverrides: {
          root: { '& .MuiTableCell-head': { backgroundColor: c.surfaceContainerHigh, color: c.onSurface, fontWeight: 500 } },
        },
      },
      MuiTableRow: {
        styleOverrides: { root: { '&:hover': { backgroundColor: alpha(c.onSurface, 0.04) } } },
      },
      MuiMenu: {
        styleOverrides: { paper: { borderRadius: 4, backgroundColor: c.surfaceContainer, backgroundImage: 'none', boxShadow: s2 } },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            minHeight: 48,
            '&:hover': { backgroundColor: alpha(c.onSurface, 0.08) },
            '&.Mui-selected': { backgroundColor: alpha(c.primary, 0.12), '&:hover': { backgroundColor: alpha(c.primary, 0.16) } },
          },
        },
      },
      MuiFab: {
        defaultProps: { color: 'primary' },
        styleOverrides: {
          root: { borderRadius: 16, boxShadow: s3 },
          primary: { backgroundColor: c.primaryContainer, color: c.onPrimaryContainer, '&:hover': { boxShadow: s3 } },
        },
      },
      MuiBadge: { styleOverrides: { badge: { backgroundColor: c.error, color: c.onError } } },
      MuiLinearProgress: {
        styleOverrides: { root: { borderRadius: 4, backgroundColor: c.surfaceVariant }, bar: { borderRadius: 4 } },
      },
      MuiSelect: { styleOverrides: { icon: { color: c.onSurfaceVariant } } },
      MuiStepLabel: { styleOverrides: { label: { '&.Mui-active': { fontWeight: 500 } } } },
    },
  };
};

export const lightTheme = createTheme(getThemeOptions('light'));
export const darkTheme  = createTheme(getThemeOptions('dark'));
